import { NavLink, useNavigate } from "react-router-dom";
import { House, FilmSlate, Books, UsersThree, AddressBook, UserCircle, ShieldCheck, Compass } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import client from "@/lib/api";
import { NavLogo } from "@/components/NavLogo";
import { QuickIcons } from "@/components/QuickIcons";
import { CommandPalette } from "@/components/CommandPalette";
import { BroadcastBanner } from "@/components/BroadcastBanner";
import { InviteRewardNudge } from "@/components/InviteRewardNudge";
import { InviteCelebrationWatcher } from "@/components/InviteCelebration";
import { AppFooter } from "@/components/AppFooter";
import worley from "@/assets/worley-avatar.png";
import { useEffect, useState } from "react";

// The primary 6-destination IA (Home / Browse / Library / Friends / Together
// / You), now the app's one and only navigation.
const PRIMARY = [
  { to: "/app", label: "Home", icon: House, end: true, testid: "ia-nav-home" },
  { to: "/app/screening-room", label: "Discover", icon: FilmSlate, testid: "ia-nav-discover" },
  { to: "/app/catalog", label: "Screening Room", icon: Compass, testid: "ia-nav-screening-room" },
  { to: "/app/library", label: "Library", icon: Books, testid: "ia-nav-library" },
  { to: "/app/friends-hub", label: "Friends", icon: AddressBook, testid: "ia-nav-friends" },
  { to: "/app/together", label: "Together", icon: UsersThree, testid: "ia-nav-together" },
  { to: "/app/you", label: "You", icon: UserCircle, testid: "ia-nav-you" },
];
function FriendsNavItem({ n, mobile }) {
  const { friendsBadge } = useNotifications();
  const navigate = useNavigate();
  const openRequests = (e) => { e.preventDefault(); e.stopPropagation(); navigate("/app/friends-hub?tab=requests"); };

  if (mobile) {
    return (
      <div className="relative flex flex-col items-center">
        <NavLink to={n.to} end={n.end} data-testid={n.testid}
          className={({ isActive }) => `flex flex-col items-center gap-0.5 px-1 py-1 text-[10px] font-bold ${isActive ? "text-gold" : "text-faint"}`}>
          {({ isActive }) => <><n.icon size={22} weight={isActive ? "fill" : "regular"} />{n.label}</>}
        </NavLink>
        {friendsBadge > 0 && (
          <button onClick={openRequests} data-testid={`${n.testid}-badge`} aria-label={`${friendsBadge} new friend requests`}
            className="absolute -right-1 -top-0.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-gold px-1 text-[9px] font-extrabold text-gold-ink">
            {friendsBadge > 9 ? "9+" : friendsBadge}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="relative">
      <NavLink to={n.to} end={n.end} data-testid={n.testid}
        className={({ isActive }) => `flex items-center gap-4 rounded-[14px] px-4 py-3 text-[15px] transition-colors duration-200 ${
          isActive ? "bg-gold/[0.16] font-bold text-[#F6F5F1]" : "font-semibold text-dim hover:bg-white/5 hover:text-[#F6F5F1]"
        }`}>
        {({ isActive }) => <><n.icon size={22} weight={isActive ? "fill" : "regular"} className={isActive ? "text-gold" : "text-dim"} />{n.label}</>}
      </NavLink>
      {friendsBadge > 0 && (
        <button onClick={openRequests} data-testid={`${n.testid}-badge`} aria-label={`${friendsBadge} new friend requests`}
          className="absolute right-4 top-1/2 flex h-[20px] min-w-[20px] -translate-y-1/2 items-center justify-center rounded-full bg-gold px-1.5 text-[11px] font-extrabold text-gold-ink transition-opacity hover:opacity-90">
          {friendsBadge > 9 ? "9+" : friendsBadge}
        </button>
      )}
    </div>
  );
}

const TOP_ITEM_CLS = (isActive) => `group relative flex flex-col items-center justify-center gap-1 whitespace-nowrap border-b-2 px-0.5 py-1 text-xs font-medium transition-colors duration-200 ${
  isActive ? "border-gold text-gold" : "border-transparent text-dim hover:border-gold/40 hover:text-[#F6F5F1]"
}`;

function NavTooltip({ label }) {
  return (
    <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 scale-95 whitespace-nowrap rounded-md bg-black/95 px-2.5 py-1 text-[11px] font-semibold text-[#F6F5F1] opacity-0 shadow-lg ring-1 ring-gold/25 transition-all duration-150 group-hover:scale-100 group-hover:opacity-100 lg:hidden">
      {label}
    </span>
  );
}

function TopItem({ n }) {
  return (
    <NavLink to={n.to} end={n.end} data-testid={n.testid} className={({ isActive }) => TOP_ITEM_CLS(isActive)}>
      {({ isActive }) => n.avatar ? (
        <><img src={worley} alt="" style={{ width: 18, height: 18 }} className={`rounded-full object-cover transition-transform duration-200 ease-out group-hover:scale-110 ${isActive ? "" : "opacity-60"}`} /><span>{n.label}</span></>
      ) : (
        <><n.icon size={18} weight={isActive ? "fill" : "regular"} className={`transition-transform duration-200 ease-out group-hover:scale-110 ${isActive ? "text-gold" : "text-dim"}`} /><span>{n.label}</span></>
      )}
    </NavLink>
  );
}

function HomeTopItem({ n, incomplete }) {
  return (
    <div className="relative">
      <NavLink to={n.to} end={n.end} data-testid={n.testid} className={({ isActive }) => TOP_ITEM_CLS(isActive)}>
        {({ isActive }) => <><n.icon size={18} weight={isActive ? "fill" : "regular"} className={`transition-transform duration-200 ease-out group-hover:scale-110 ${isActive ? "text-gold" : "text-dim"}`} /><span>{n.label}</span></>}
      </NavLink>
      {incomplete && (
        <span data-testid="ia-nav-home-setup-dot" aria-label="Getting started setup incomplete"
          className="absolute right-1.5 top-1.5 h-[7px] w-[7px] rounded-full bg-gold shadow-[0_0_6px_2px_rgba(212,175,55,0.55)]" />
      )}
    </div>
  );
}

function FriendsTopItem({ n }) {
  const { friendsBadge } = useNotifications();
  const navigate = useNavigate();
  const openRequests = (e) => { e.preventDefault(); e.stopPropagation(); navigate("/app/friends-hub?tab=requests"); };
  return (
    <div className="relative">
      <NavLink to={n.to} end={n.end} data-testid={n.testid} className={({ isActive }) => TOP_ITEM_CLS(isActive)}>
        {({ isActive }) => <><n.icon size={18} weight={isActive ? "fill" : "regular"} className={`transition-transform duration-200 ease-out group-hover:scale-110 ${isActive ? "text-gold" : "text-dim"}`} /><span>{n.label}</span></>}
      </NavLink>
      {friendsBadge > 0 && (
        <button onClick={openRequests} data-testid={`${n.testid}-badge`} aria-label={`${friendsBadge} new friend requests`}
          className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gold px-1 text-[10px] font-extrabold text-gold-ink transition-opacity hover:opacity-90">
          {friendsBadge > 9 ? "9+" : friendsBadge}
        </button>
      )}
    </div>
  );
}

export function NewRail({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [setupIncomplete, setSetupIncomplete] = useState(false);

  useEffect(() => {
    client.get("/onboarding/status").then((r) => setSetupIncomplete(!r.data.all_resolved)).catch(() => {});
  }, []);

  useEffect(() => {
    const openPalette = () => setPaletteOpen(true);
    window.addEventListener("open-command-palette", openPalette);
    return () => window.removeEventListener("open-command-palette", openPalette);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="grain flex min-h-screen bg-obsidian">
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <InviteCelebrationWatcher />

      <header data-testid="ia-nav-top-experiment" className={`fixed inset-x-0 top-0 z-30 hidden bg-obsidian/70 bg-gradient-to-b from-white/[0.06] via-transparent to-transparent backdrop-blur-2xl backdrop-saturate-150 shadow-[inset_0_1px_0_rgba(255,255,255,0.09)] lg:flex transition-[padding] duration-300 ${scrolled ? "py-1 lg:py-1.5" : "py-2 lg:py-2.5"}`}>
        <div className="mx-auto flex w-full max-w-sc-layout items-center gap-1 lg:gap-2 px-4 md:px-8">
        <button onClick={() => navigate("/app")} data-testid="ia-nav-brand" className="shrink-0 transition-transform hover:scale-[1.03]">
          <NavLogo className={`w-auto transition-[height] duration-300 ${scrolled ? "h-14 lg:h-16" : "h-20 lg:h-24"}`} />
        </button>
        <div className="flex-1" />
        <nav className="flex shrink-0 items-center gap-5 xl:gap-7">
          {PRIMARY.filter((n) => n.to !== "/app/you").map((n) => n.to === "/app/friends-hub" ? <FriendsTopItem key={n.to} n={n} />
            : n.to === "/app" ? <HomeTopItem key={n.to} n={n} incomplete={setupIncomplete} />
            : <TopItem key={n.to} n={n} />)}
        </nav>
        <div className="flex-1" />
        <span className="mx-0.5 lg:mx-0.5 xl:mx-1 h-6 w-px shrink-0 bg-white/[0.08]" />
        {user?.is_admin && <TopItem n={{ to: "/app/admin/console", label: "Admin", icon: ShieldCheck, testid: "ia-nav-admin" }} />}
        <QuickIcons variant="bar" hideScreeningRoom />
        <button onClick={() => navigate("/app/you")} data-testid="ia-nav-profile"
          className="group relative ml-0.5 lg:ml-0.5 xl:ml-1 flex shrink-0 items-center gap-1.5 lg:gap-1.5 xl:gap-2 rounded-full py-1 lg:py-1 xl:py-1.5 pl-1 lg:pl-1 xl:pl-1.5 pr-1 lg:pr-2 xl:pr-3 text-left text-dim transition-colors hover:bg-white/5 hover:text-[#F6F5F1]">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-6 w-6 lg:h-7 lg:w-7 xl:h-8 xl:w-8 rounded-full object-cover" />
          ) : (
            <div className="flex h-6 w-6 lg:h-7 lg:w-7 xl:h-8 xl:w-8 items-center justify-center rounded-full bg-gold/[0.16] font-display text-gold">{user?.name?.[0]?.toUpperCase()}</div>
          )}
          <p className="hidden xl:block max-w-[140px] truncate text-sm text-[#F6F5F1]">{user?.name}</p>
          <NavTooltip label="Your profile" />
        </button>
        </div>
        <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-transparent via-gold/80 to-transparent" />
      </header>

      {/* QuickIcons "float" variant intentionally omitted here — it's redundant
          with the new top header's own search/notifications icons and was
          colliding with them visually. Still used via variant="bar" below
          for mobile. No standalone sign-out button in the header anymore —
          clicking the profile name goes to /app/you, which has a deliberate
          Sign out action, so it can't be hit by accident. */}

      <div className="fixed inset-x-0 top-0 z-30 flex items-center justify-between border-b border-white/[0.08] bg-obsidian/80 px-4 py-3 backdrop-blur-xl md:px-8 lg:hidden">
        <NavLogo height={84} />
        <div className="flex shrink-0 items-center gap-2">
          <QuickIcons variant="bar" showAvatar />
        </div>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-around border-t border-white/[0.08] bg-obsidian/95 py-2 backdrop-blur-xl lg:hidden">
        {PRIMARY.map((n) => n.to === "/app/friends-hub" ? (
          <FriendsNavItem key={n.to} n={{ ...n, testid: `mobile-${n.testid}` }} mobile />
        ) : (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`mobile-${n.testid}`}
            className={({ isActive }) => `relative flex flex-col items-center gap-0.5 px-1 py-1 text-[10px] font-bold ${isActive ? "text-gold" : "text-faint"}`}>
            {({ isActive }) => n.avatar
              ? <><img src={worley} alt="" style={{ width: 22, height: 22 }} className={`rounded-full ${isActive ? "" : "opacity-60"}`} />{n.label}</>
              : <>
                <n.icon size={22} weight={isActive ? "fill" : "regular"} />{n.label}
                {n.to === "/app" && setupIncomplete && (
                  <span data-testid="mobile-ia-nav-home-setup-dot" aria-label="Getting started setup incomplete"
                    className="absolute right-0.5 top-0 h-[7px] w-[7px] rounded-full bg-gold shadow-[0_0_6px_2px_rgba(212,175,55,0.55)]" />
                )}
              </>}
          </NavLink>
        ))}
      </nav>

      <main className="relative z-10 min-h-screen min-w-0 flex-1 pb-24 pt-32">
        <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 opacity-60" style={{ background: "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(212,175,55,0.05), transparent 70%)" }} />
        {/* Intentionally no BackBar here — the persistent nav handles navigation (per spec) */}
        <div className="mx-auto max-w-sc-layout px-4 md:px-8">
          <BroadcastBanner />
          <InviteRewardNudge />
          {children}
          <AppFooter />
        </div>
      </main>
    </div>
  );
}
